import React from 'react';
import Icon from '../../../components/AppIcon';

const RoleListItem = ({ role, onClick, viewMode = 'full', isFeatured, onFeatureToggle, isFavorited, onFavoriteToggle, isSuperUser }) => {
  const handleClick = () => {
    onClick?.(role);
  };

  const formatVocalRange = (range) => {
    if (!range) return 'Not specified';
    return range;
  };

  const formatVoiceType = (voiceType) => {
    if (!voiceType || voiceType === 'speaking') return 'Speaking';
    return voiceType?.split('_')?.map(word => 
      word?.charAt(0)?.toUpperCase() + word?.slice(1)
    )?.join(' ');
  };

  const formatRoleType = (roleType) => {
    if (!roleType) return 'Ensemble';
    return roleType?.charAt(0)?.toUpperCase() + roleType?.slice(1);
  };

  const getPosterUrl = () => {
    return role?.musicals?.poster_url || '/assets/images/no_image.png';
  };

  // Compact view component
  if (viewMode === 'compact') {
    return (
      <div 
        onClick={handleClick}
        className="group bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-lg p-3 hover:bg-opacity-15 hover:border-opacity-30 transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Poster thumbnail and basic info */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {/* Compact Poster */}
            <div className="flex-shrink-0 w-10 h-12 rounded overflow-hidden bg-white bg-opacity-10">
              <img
                src={getPosterUrl()}
                alt={`${role?.musicals?.title || 'Musical'} poster`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                }}
              />
            </div>

            {/* Role info - single line */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 text-sm">
                <span className="font-semibold text-white truncate">{role?.character_name || 'Unknown Character'}</span>
                <span className="text-blue-300">•</span>
                <span className="text-blue-200 truncate">{role?.musicals?.title || 'Unknown Musical'}</span>
                
                {role?.song_count > 0 && (
                  <>
                    <span className="text-blue-300">•</span>
                    <span className="text-blue-200 flex-shrink-0">{role?.song_count} song{role?.song_count !== 1 ? 's' : ''}</span>
                  </>
                )}

                {role?.vocal_range && role?.vocal_range !== 'Not specified' && (
                  <>
                    <span className="text-blue-300">•</span>
                    <span className="text-green-200 flex-shrink-0">{role?.vocal_range}</span>
                  </>
                )}

                {role?.is_principal && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-500 bg-opacity-20 text-yellow-200 border border-yellow-400 border-opacity-30 flex-shrink-0">
                    Principal
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Star, Heart, and chevron */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isSuperUser?.() && onFeatureToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFeatureToggle(role); }}
                className="p-1 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
              >
                <Icon
                  name="Star"
                  size={14}
                  className={isFeatured ? 'text-yellow-400' : 'text-blue-300 opacity-60'}
                  style={isFeatured ? { fill: 'currentColor' } : {}}
                />
              </button>
            )}
            {onFavoriteToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFavoriteToggle(role); }}
                className="p-1 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Icon
                  name="Heart"
                  size={14}
                  className={isFavorited ? 'text-red-400' : 'text-blue-300 opacity-60'}
                  style={isFavorited ? { fill: 'currentColor' } : {}}
                />
              </button>
            )}
            <Icon 
              name="ChevronRight" 
              size={16} 
              className="text-blue-200 group-hover:text-purple-200 transition-colors duration-200" 
            />
          </div>
        </div>
      </div>
    );
  }

  // Full view (existing implementation)
  return (
    <div 
      onClick={handleClick}
      className="group bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 hover:bg-opacity-15 hover:border-opacity-30 transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start space-x-4">
        {/* Musical Poster */}
        <div className="flex-shrink-0 w-16 h-24 rounded-lg overflow-hidden bg-white bg-opacity-10">
          <img
            src={getPosterUrl()}
            alt={`${role?.musicals?.title || 'Musical'} poster`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = '/assets/images/no_image.png';
            }}
          />
        </div>

        {/* Role Information */}
        <div className="flex-grow min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-grow">
              <h3 className="text-xl font-heading font-semibold text-white mb-1 group-hover:text-purple-200 transition-colors duration-200">
                {role?.character_name || 'Unknown Character'}
              </h3>
              <p className="text-blue-200 font-body text-sm">
                from <span className="font-medium">{role?.musicals?.title || 'Unknown Musical'}</span>
                {role?.musicals?.composer && (
                  <span className="text-blue-300"> • Music by {role?.musicals?.composer}</span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0 ml-4">
              {isSuperUser?.() && onFeatureToggle && (
                <button
                  onClick={(e) => { e?.stopPropagation(); onFeatureToggle(role); }}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                  title={isFeatured ? 'Remove from featured' : 'Mark as featured'}
                >
                  <Icon
                    name="Star"
                    size={16}
                    className={isFeatured ? 'text-yellow-400' : 'text-blue-300 opacity-60'}
                    style={isFeatured ? { fill: 'currentColor' } : {}}
                  />
                </button>
              )}
              {onFavoriteToggle && (
                <button
                  onClick={(e) => { e?.stopPropagation(); onFavoriteToggle(role); }}
                  className="p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                  title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Icon
                    name="Heart"
                    size={16}
                    className={isFavorited ? 'text-red-400' : 'text-blue-300 opacity-60'}
                    style={isFavorited ? { fill: 'currentColor' } : {}}
                  />
                </button>
              )}
              <Icon 
                name="ChevronRight" 
                size={20} 
                className="text-blue-200 group-hover:text-purple-200 transition-colors duration-200" 
              />
            </div>
          </div>

          {/* Role Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            {/* Song Count */}
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-500 bg-opacity-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Music" size={12} className="text-blue-200" />
              </div>
              <div>
                <div className="text-white font-medium text-sm">
                  {role?.song_count || 0} song{(role?.song_count || 0) !== 1 ? 's' : ''}
                </div>
                <div className="text-blue-300 text-xs font-caption">
                  Featured in
                </div>
              </div>
            </div>

            {/* Vocal Range */}
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-green-500 bg-opacity-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Volume2" size={12} className="text-green-200" />
              </div>
              <div>
                <div className="text-white font-medium text-sm">
                  {formatVocalRange(role?.vocal_range)}
                </div>
                <div className="text-blue-300 text-xs font-caption">
                  Vocal range
                </div>
              </div>
            </div>

            {/* Voice Type & Role Type */}
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-purple-500 bg-opacity-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={12} className="text-purple-200" />
              </div>
              <div>
                <div className="text-white font-medium text-sm">
                  {formatVoiceType(role?.voice_type)}
                </div>
                <div className="text-blue-300 text-xs font-caption">
                  {formatRoleType(role?.role_type)} role
                </div>
              </div>
            </div>
          </div>

          {/* Character Description */}
          {role?.description && (
            <p className="text-blue-100 text-sm font-body line-clamp-2 mb-2">
              {role?.description}
            </p>
          )}

          {/* Additional Info */}
          <div className="flex items-center justify-between text-xs text-blue-300">
            <div className="flex items-center space-x-4">
              {role?.is_principal && (
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={12} className="text-yellow-400" />
                  <span>Principal role</span>
                </div>
              )}
              {role?.age_range && (
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={12} />
                  <span>Age: {role?.age_range}</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-1">
              <Icon name="Theater" size={12} />
              <span>{role?.musicals?.year_premiered || 'Unknown'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleListItem;